import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatMoney, todayISO } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ShoppingCart, PackageOpen } from "lucide-react";

const CATEGORY_LABEL = {
  welding_rod: "Welding Rod",
  abrasive: "Abrasive",
  cement: "Cement",
  diesel: "Diesel",
  other: "Other",
};

export default function IndustrialEntry({ type }) {
  // type = "purchase" | "sale"
  const isPurchase = type === "purchase";
  const partyLabel = isPurchase ? "Vendor" : "Customer";
  const partyType = isPurchase ? "vendor" : "customer";
  const Icon = isPurchase ? PackageOpen : ShoppingCart;

  const [items, setItems] = useState([]);        // master items
  const [parties, setParties] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [billNo, setBillNo] = useState("");
  const [note, setNote] = useState("");
  const [rows, setRows] = useState([{ item_id: "", qty: "", rate: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [i, p] = await Promise.all([
          api.get("/industrial/items"),
          api.get(`/parties?type=${partyType}`),
        ]);
        setItems(i.data);
        setParties(p.data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [partyType]);

  const total = useMemo(() =>
    rows.reduce((sum, r) => sum + (Number(r.qty) || 0) * (Number(r.rate) || 0), 0)
  , [rows]);

  const updateRow = (idx, patch) => {
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = () => setRows([...rows, { item_id: "", qty: "", rate: "" }]);
  const delRow = (idx) => setRows(rows.length > 1 ? rows.filter((_, i) => i !== idx) : rows);

  const submit = async (e) => {
    e.preventDefault();
    if (!partyId) return toast.error(`Select a ${partyLabel.toLowerCase()}`);
    const clean = rows
      .filter((r) => r.item_id && Number(r.qty) > 0)
      .map((r) => ({ item_id: r.item_id, qty: Number(r.qty), rate: Number(r.rate) || 0 }));
    if (clean.length === 0) return toast.error("Add at least one item with qty");
    setSaving(true);
    try {
      await api.post("/industrial/transactions", {
        type, date, party_id: partyId, items: clean, note, bill_no: billNo,
      });
      toast.success(`${isPurchase ? "Purchase" : "Sale"} recorded`);
      setRows([{ item_id: "", qty: "", rate: "" }]);
      setNote(""); setBillNo("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`industrial-${type}-form`}>
      <p className="small-label text-gray-500 flex items-center gap-2">
        <Icon size={14} />
        {isPurchase
          ? "Record purchase of industrial supplies. Adds to stock and vendor's payable."
          : "Record sale of industrial supplies. Reduces stock and adds to customer's receivable."}
      </p>

      <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="small-label text-gray-500">Date</label>
          <input type="date" className="input-lg w-full" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`ind-${type}-date`} />
        </div>
        <div className="md:col-span-2">
          <label className="small-label text-gray-500">{partyLabel} *</label>
          <select className="input-lg w-full" value={partyId} onChange={(e) => setPartyId(e.target.value)} data-testid={`ind-${type}-party`}>
            <option value="">— Select {partyLabel} —</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="small-label text-gray-500">Bill No.</label>
          <input className="input-lg w-full" value={billNo} onChange={(e) => setBillNo(e.target.value)} placeholder="Optional" data-testid={`ind-${type}-billno`} />
        </div>
      </div>

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="font-heading font-bold text-lg">Items</div>
          <button type="button" className="btn-secondary flex items-center gap-1" onClick={addRow} data-testid={`ind-${type}-add-row`}>
            <Plus size={14} /> Add Row
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left small-label text-gray-500">
              <tr>
                <th className="px-3 py-2">Item</th>
                <th className="px-3 py-2 w-24">Unit</th>
                <th className="px-3 py-2 w-28">Qty</th>
                <th className="px-3 py-2 w-32">Rate ₹</th>
                <th className="px-3 py-2 w-32 text-right">Amount ₹</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((r, idx) => {
                const item = itemMap[r.item_id];
                const amount = (Number(r.qty) || 0) * (Number(r.rate) || 0);
                return (
                  <tr key={idx}>
                    <td className="px-3 py-2">
                      <select className="input-lg w-full" value={r.item_id}
                        onChange={(e) => updateRow(idx, { item_id: e.target.value })}
                        data-testid={`ind-${type}-item-${idx}`}>
                        <option value="">— Select Item —</option>
                        {items.map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.name}{it.brand ? ` · ${it.brand}` : ""} ({CATEGORY_LABEL[it.category] || it.category})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 uppercase text-xs tracking-wide">{item?.unit || "—"}</td>
                    <td className="px-3 py-2">
                      <input type="number" min="0" step="0.01" className="input-lg w-full"
                        value={r.qty} onChange={(e) => updateRow(idx, { qty: e.target.value })}
                        data-testid={`ind-${type}-qty-${idx}`} />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" min="0" step="0.01" className="input-lg w-full"
                        value={r.rate} onChange={(e) => updateRow(idx, { rate: e.target.value })}
                        data-testid={`ind-${type}-rate-${idx}`} />
                    </td>
                    <td className="px-3 py-2 text-right data-num font-semibold">₹ {formatMoney(amount)}</td>
                    <td className="px-3 py-2 text-right">
                      <button type="button" className="text-red-500 hover:text-red-700"
                        onClick={() => delRow(idx)} data-testid={`ind-${type}-del-row-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right font-heading font-bold">Total</td>
                <td className="px-3 py-2 text-right data-num font-bold text-lg" data-testid={`ind-${type}-total`}>
                  ₹ {formatMoney(total)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="card-flat p-4">
        <label className="small-label text-gray-500">Note</label>
        <textarea className="input-lg w-full" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Optional remarks..." data-testid={`ind-${type}-note`} />
      </div>

      <div className="flex gap-2">
        <button type="submit" disabled={saving} className="btn-primary" data-testid={`ind-${type}-submit`}>
          {saving ? "Saving..." : `Record ${isPurchase ? "Purchase" : "Sale"}`}
        </button>
      </div>
    </form>
  );
}
